import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";

import { useCreateMaintenanceWindowMutation } from "../api/maintenance.api";
import { maintenanceMessages } from "../maintenance.intl";
import { MaintenanceScheduleSchema } from "../schemas/MaintenanceSchema";
import type { MaintenanceScheduleDraft } from "../types/maintenance.types";
import { buildCreateMaintenancePayload } from "../utils/maintenanceTime";

import MaintenanceForm from "./MaintenanceForm";
import MaintenanceReviewDialog from "./MaintenanceReviewDialog";

import { useIntlMessages } from "@/app/hooks/useIntlMessages";
import { customToast } from "@/components/alerts/CustomToast";
import FormDialog from "@/components/forms/FormDialog";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { validateResponse } from "@/utils/form/formHelper";

const INITIAL_DRAFT: MaintenanceScheduleDraft = {
  startDate: undefined as unknown as Date,
  startTime: undefined as unknown as Date,
  endDate: undefined,
  endTime: undefined,
  reminderType: "CUSTOM",
  reminderDate: undefined,
  reminderTime: undefined,
  notice: "",
};

interface MaintenanceCreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const MaintenanceCreateDialog = (
  props: MaintenanceCreateDialogProps,
): React.JSX.Element => {
  const { isOpen, onClose, onSuccess } = props;
  const t = useIntlMessages(maintenanceMessages);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const form = useForm<MaintenanceScheduleDraft>({
    resolver: zodResolver(MaintenanceScheduleSchema),
    defaultValues: INITIAL_DRAFT,
  });
  const [createMaintenance, { isLoading }] =
    useCreateMaintenanceWindowMutation();

  const handleExit = useCallback((): void => {
    form.reset(INITIAL_DRAFT);
    setIsReviewOpen(false);
    onClose();
  }, [form, onClose]);

  const handleReview = useCallback((): void => {
    setIsReviewOpen(true);
  }, []);

  const handlePublish = useCallback(
    async (draft: MaintenanceScheduleDraft): Promise<void> => {
      const payload = buildCreateMaintenancePayload(draft);
      const response = await createMaintenance({ data: payload });

      validateResponse({
        response,
        onSuccess: (message) => {
          customToast.success(message ?? t("maintenanceCreated"));
          form.reset(INITIAL_DRAFT);
          setIsReviewOpen(false);
          onSuccess();
          onClose();
        },
      });
    },
    [createMaintenance, form, onClose, onSuccess, t],
  );

  const handleReset = useCallback((): void => {
    form.reset(INITIAL_DRAFT);
  }, [form]);

  return (
    <FormDialog
      isOpen={isOpen}
      onClose={handleExit}
      title={t("createMaintenance")}
      isFormDirty={form.formState.isDirty}
      className='sm:max-w-lg md:max-w-5xl'
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleReview)}>
          <MaintenanceForm
            form={form}
            onReview={handleReview}
            onCancel={handleExit}
            hideActions
          />

          <div className='flex w-full justify-between my-2 pb-6'>
            <Button
              variant='ghost'
              className='font-normal hover:bg-transparent'
              type='button'
              onClick={handleReset}
            >
              {t("reset")}
            </Button>
            <Button type='submit' isLoading={isLoading}>
              {t("review")}
            </Button>
          </div>
        </form>
        <MaintenanceReviewDialog
          open={isReviewOpen}
          onClose={() => setIsReviewOpen(false)}
          onPublish={handlePublish}
          isPublishing={isLoading}
        />
      </Form>
    </FormDialog>
  );
};

export { MaintenanceCreateDialog };
